/**
 *
 * MIT License
 *
 * Copyright (c) 2017 Erik Rasmussen
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
/**
 * Duplicated from https://github.com/final-form/react-final-form-arrays/blob/a19c686b60e54720e9bd09cc559f94dfdcb83f87/src/useFieldArray.js
 *
 * The original does not support Promise validation correctly: https://github.com/final-form/react-final-form-arrays/issues/176
 *
 * search for `!!!` to retrieve changes
 */
import * as React from 'react';
import { fieldSubscriptionItems, ARRAY_ERROR } from 'final-form';

// eslint-disable-next-line @gandi/gandi/no-private-import

import { useForm, useField } from 'react-final-form';
// eslint-disable-next-line @gandi/gandi/no-private-import

// $FlowFixMe[unclear-type]
const defaultIsEqual = (aArray, bArray) => aArray === bArray || Array.isArray(aArray) && Array.isArray(bArray) && aArray.length === bArray.length && !aArray.some((a, index) => a !== bArray[index]);
function useConstant(init) {
  const ref = React.useRef();
  if (!ref.current) {
    ref.current = init();
  }
  return ref.current;
}
const all = fieldSubscriptionItems.reduce((result, key) => {
  // $FlowFixMe[prop-missing]
  result[key] = true; // eslint-disable-line no-param-reassign
  return result;
}, {});

// eslint-disable-next-line no-unused-vars

/**
 * !!! 3rd change, refactor error handling for synced error
 */
const handleError = error => {
  if (!error || Array.isArray(error)) {
    return error;
  }
  const arrayError = [];
  // gross, but we have to set a string key on the array
  // $FlowFixMe[unclear-type]
  arrayError[ARRAY_ERROR] = error;
  return arrayError;
};
export const useFieldArray = (name, {
  subscription = all,
  defaultValue,
  initialValue,
  isEqual = defaultIsEqual,
  validate: validateProp
} = {}) => {
  const form = useForm('useFieldArray');

  // $FlowFixMe[incompatible-indexer]
  const formMutators = form.mutators;
  const hasMutators = !!(formMutators && formMutators.push && formMutators.pop);
  if (!hasMutators) {
    throw new Error('Array mutators not found. You need to provide the mutators from final-form-arrays to your form');
  }
  const mutators = React.useMemo(() =>
  // curry the field name onto all mutator calls
  // $FlowFixMe[incompatible-call]
  Object.keys(formMutators).reduce((result, key) => {
    // $FlowFixMe[extra-arg]
    // $FlowFixMe[incompatible-call]
    result[key] = (...args) => formMutators[key](name, ...args); // eslint-disable-line no-param-reassign
    return result;
  }, {}), [name, formMutators]);
  const validate = useConstant(() => (value, allValues, meta) => {
    if (!validateProp) return undefined;
    // !!! First change, store validation result
    const validation = validateProp(value, allValues, meta);
    if (!validation) {
      return undefined;
    }

    // !!! Second change, resolve eventual Promise before testing if it's a valid error or an array
    if (validation?.then) {
      return validation.then(error => handleError(error));
    }
    return handleError(validation);
  });
  const {
    meta: {
      length,
      ...meta
    },
    input,
    ...fieldState
  } = useField(name,
  // $FlowFixMe[prop-missing] FinalForm's UseFieldConfig definition isn't compatible with exact_by_default
  {
    subscription: {
      ...subscription,
      length: true
    },
    defaultValue,
    initialValue,
    isEqual,
    validate,
    format: v => v
  });
  const forEach = iterator => {
    // required || for Flow, but results in uncovered line in Jest/Istanbul
    // istanbul ignore next
    const len = length || 0;
    for (let i = 0; i < len; i += 1) {
      iterator(`${name}[${i}]`, i);
    }
  };

  // $FlowFixMe[unclear-type]
  const map = iterator => {
    // required || for Flow, but results in uncovered line in Jest/Istanbul
    // istanbul ignore next
    const len = length || 0;
    // $FlowFixMe[unclear-type]
    const results = [];
    for (let i = 0; i < len; i += 1) {
      results.push(iterator(`${name}[${i}]`, i));
    }
    return results;
  };

  // $FlowFixMe[prop-missing] FinalForm's FieldArrayProps definition isn't compatible with exact_by_default
  return {
    fields:
    // $FlowFixMe[incompatible-return]
    {
      name,
      forEach,
      length: length || 0,
      map,
      ...mutators,
      ...fieldState,
      value: input.value
    },
    meta
  };
};
export default useFieldArray;